import React, { useState, useEffect } from 'react';
import axios from '../../services/axios';

const CommercialDashboard = () => {
  const [data, setData] = useState({
    originalCV: {
      prelims: 0,
      workDone: 0,
      provisionalSums: 0,
      approvedVariation: 0,
    },
    ipc: {
      prelims: 0,
      workDone: 0,
      provisionalSums: 0,
      approvedVariation: 0,
      materialOnSite: 0,
      claimsOtherAmounts: 0,
      advancesAdjustments: 0,
      deductionsPenalty: 0,
      prevCertified: 0,
    },
    ipa: {
      prelims: 0,
      workDone: 0,
      provisionalSums: 0,
      approvedVariation: 0,
      materialOnSite: 0,
      claimsOtherAmounts: 0,
      advancesAdjustments: 0,
      deductionsPenalty: 0,
      prevCertified: 0,
    },
    vatPercent: 0,
  });

  const [calculated, setCalculated] = useState({
    originalCV: { subTotalA: 0, subTotalB: 0, netPayable: 0, vat: 0, payableAmount: 0 },
    ipc: { subTotalA: 0, subTotalB: 0, netPayable: 0, netAmountDue: 0, vat: 0, payableAmount: 0 },
    ipa: { subTotalA: 0, subTotalB: 0, netPayable: 0, netAmountDue: 0, vat: 0, payableAmount: 0 },
  });

  const [loading, setLoading] = useState(true);

  const toNumber = (val) => Number(val || 0);

  const [references, setReferences] = useState({
    ipc: null,
    ipa: null,
  });

  useEffect(() => {
    // Original CV Calculations
    const cvSubTotalA = toNumber(data.originalCV.prelims) + 
                        toNumber(data.originalCV.workDone) + 
                        toNumber(data.originalCV.provisionalSums) + 
                        toNumber(data.originalCV.approvedVariation);
    const cvVat = (cvSubTotalA * toNumber(data.vatPercent)) / 100;
    const cvPayable = cvSubTotalA + cvVat;

    // IPC Calculations
    const ipcSubTotalA = toNumber(data.ipc.prelims) + 
                         toNumber(data.ipc.workDone) + 
                         toNumber(data.ipc.provisionalSums) + 
                         toNumber(data.ipc.approvedVariation);
    
    const ipcSubTotalB = toNumber(data.ipc.materialOnSite) + 
                         toNumber(data.ipc.claimsOtherAmounts) + 
                         toNumber(data.ipc.advancesAdjustments) + 
                         toNumber(data.ipc.deductionsPenalty);
    
    const ipcNetPayable = ipcSubTotalA + ipcSubTotalB;
    const ipcNetAmountDue = ipcNetPayable - toNumber(data.ipc.prevCertified);
    const ipcVat = (ipcNetAmountDue * toNumber(data.vatPercent)) / 100;
    const ipcPayable = ipcNetAmountDue + ipcVat;

    // IPA Calculations
    const ipaSubTotalA = toNumber(data.ipa.prelims) + 
                         toNumber(data.ipa.workDone) + 
                         toNumber(data.ipa.provisionalSums) + 
                         toNumber(data.ipa.approvedVariation);
    
    const ipaSubTotalB = toNumber(data.ipa.materialOnSite) + 
                         toNumber(data.ipa.claimsOtherAmounts) + 
                         toNumber(data.ipa.advancesAdjustments) + 
                         toNumber(data.ipa.deductionsPenalty);
    
    const ipaNetPayable = ipaSubTotalA + ipaSubTotalB;
    const ipaNetAmountDue = ipaNetPayable - toNumber(data.ipa.prevCertified);
    const ipaVat = (ipaNetAmountDue * toNumber(data.vatPercent)) / 100;
    const ipaPayable = ipaNetAmountDue + ipaVat;

    setCalculated({
      originalCV: { 
        subTotalA: cvSubTotalA, 
        subTotalB: 0, 
        netPayable: cvSubTotalA, 
        vat: cvVat, 
        payableAmount: cvPayable 
      },
      ipc: { 
        subTotalA: ipcSubTotalA, 
        subTotalB: ipcSubTotalB, 
        netPayable: ipcNetPayable, 
        netAmountDue: ipcNetAmountDue,
        vat: ipcVat, 
        payableAmount: ipcPayable 
      },
      ipa: { 
        subTotalA: ipaSubTotalA, 
        subTotalB: ipaSubTotalB, 
        netPayable: ipaNetPayable, 
        netAmountDue: ipaNetAmountDue,
        vat: ipaVat, 
        payableAmount: ipaPayable 
      },
    });
  }, [data]);

  const formatNumber = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  useEffect(() => {
    const fetchCommercialSettings = async () => {
      try {
        const project = JSON.parse(localStorage.getItem('Project')) 
        const res = await axios.post('/fetch-commercialSettings', {
          projectName: project,
        });

        const commercial = res.data.commercial;

        setData(prev => ({
          ...prev,
          originalCV: {
            prelims: commercial.preliminaries || 0,
            workDone: commercial.workDone || 0,
            provisionalSums: commercial.provisionalSums || 0,
            approvedVariation: 0,
          },
          vatPercent: commercial.vatPercent || 0,
        }));
      } catch (error) {
        console.error('Failed to fetch commercial settings', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommercialSettings();
  }, []);

  useEffect(() => {
    const fetchIPAIPC = async () => {
      try {
        const project = JSON.parse(localStorage.getItem('Project'));

        const res = await axios.post('/fetch-IPA-IPC-PR', {
          projectName: project
        });

        const ipaData = res.data?.ipa?.commercialValues?.[0];
        const ipcData = res.data?.ipc?.commercialValues?.[0];

        setReferences({
          ipa: res.data?.ipa?.documentReference || ipaData?.reference || null,
          ipc: res.data?.ipc?.documentReference || ipcData?.reference || null,
        });

        setData(prev => ({
          ...prev,

          ipc: ipcData
            ? {
                prelims: ipcData.preliminaries || 0,
                workDone: ipcData.workDone || 0,
                provisionalSums: ipcData.provisionalSums || 0,
                approvedVariation: ipcData.approvedVariation || 0,
                materialOnSite: ipcData.materialsOnSite || 0,
                claimsOtherAmounts: ipcData.claimsAmounts || 0,
                advancesAdjustments: ipcData.advancesAdjustments || 0,
                deductionsPenalty: ipcData.deductionsPenalty || 0,
                prevCertified: ipcData.lessPrevCertified || 0,
              }
            : prev.ipc,

          ipa: ipaData
            ? {
                prelims: ipaData.preliminaries || 0,
                workDone: ipaData.workDone || 0,
                provisionalSums: ipaData.provisionalSums || 0,
                approvedVariation: ipaData.approvedVariation || 0,
                materialOnSite: ipaData.materialsOnSite || 0,
                claimsOtherAmounts: ipaData.claimsAmounts || 0,
                advancesAdjustments: ipaData.advancesAdjustments || 0,
                deductionsPenalty: ipaData.deductionsPenalty || 0,
                prevCertified: ipaData.lessPrevCertified || 0,
              }
            : prev.ipa,
        }));

      } catch (error) {
        console.error('Failed to fetch IPA / IPC', error);
      }
    };

    fetchIPAIPC();
  }, []);

  const TableRow = ({
    label,
    originalValue,
    ipcValue,
    ipaValue,
    isBold = false,
    hasBorder = false,
    isSubtotal = false,
    isHighlight = false
  }) => (
    <tr className={`
      transition-all duration-200 hover:bg-blue-50/50
      ${hasBorder ? 'border-t-2 border-blue-200' : 'border-t border-gray-100'}
      ${isSubtotal ? 'bg-gradient-to-r from-blue-50/70 to-indigo-50/70' : ''}
      ${isHighlight ? 'bg-gradient-to-r from-emerald-50 to-teal-50' : ''}
    `}>
      <td className={`py-4 px-6 text-left ${isBold ? 'font-semibold text-gray-800' : 'text-gray-700'} bg-gradient-to-r from-gray-50 to-transparent`}>
        {label}
      </td>

      <td className={`py-4 px-6 text-right ${isBold ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
        {originalValue !== null && originalValue !== undefined
          ? formatNumber(originalValue)
          : <span className="text-gray-300">-</span>}
      </td>

      <td className={`py-4 px-6 text-right ${isBold ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
        {ipcValue !== null && ipcValue !== undefined
          ? formatNumber(ipcValue)
          : <span className="text-gray-300">-</span>}
      </td>

      <td className={`py-4 px-6 text-right ${isBold ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
        {ipaValue !== null && ipaValue !== undefined
          ? formatNumber(ipaValue)
          : <span className="text-gray-300">-</span>}
      </td>
    </tr>
  );

  const SummaryCard = ({ title, amount, color = 'blue' }) => {
    const colorClasses = {
      blue: 'from-blue-500 to-blue-600',
      green: 'from-emerald-500 to-emerald-600',
      purple: 'from-purple-500 to-purple-600'
    };

    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <p className={`text-3xl font-bold bg-gradient-to-r ${colorClasses[color]} bg-clip-text text-transparent`}>
              {formatNumber(amount)}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${colorClasses[color]} opacity-10`}></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <SummaryCard 
            title="Original CV Total" 
            amount={calculated.originalCV.payableAmount}
            color="blue"
          />
          <SummaryCard 
            title="IPC Payable" 
            amount={calculated.ipc.payableAmount}
            color="green"
          />
          <SummaryCard 
            title="IPA Payable" 
            amount={calculated.ipa.payableAmount}
            color="purple"
          />
        </div>

        {/* Main Table Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {/* Table Header */}
          {/* <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
            <h2 className="text-xl font-semibold text-white">Payment Certificate Details</h2>
          </div> */}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b-2 border-blue-200">
                  <th className="py-5 px-6 text-left font-semibold text-gray-700 text-sm uppercase tracking-wider">
                    Description
                  </th>
                  <th className="py-5 px-6 text-center font-semibold text-gray-700">
                    <div className="flex flex-col items-center">
                      <span className="text-base font-bold text-blue-600">Original CV</span>
                      <span className="text-xs font-normal text-gray-500 mt-1">Contract Value</span>
                    </div>
                  </th>
                  <th className="py-5 px-6 text-center font-semibold text-gray-700">
                    <div className="flex flex-col items-center">
                      <span className="text-base font-bold text-emerald-600">IPC</span>
                      <span className="text-xs font-normal text-gray-500 mt-1">Interim Payment Certificate</span>
                      {references.ipc && (
                        <span className="text-[11px] text-gray-400 mt-1">
                          Ref: {references.ipc}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="py-5 px-6 text-center font-semibold text-gray-700">
                    <div className="flex flex-col items-center">
                      <span className="text-base font-bold text-purple-600">IPA</span>
                      <span className="text-xs font-normal text-gray-500 mt-1">Interim Payment Application</span>
                      {references.ipa && (
                        <span className="text-[11px] text-gray-400 mt-1">
                          Ref: {references.ipa}
                        </span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Section A Header */}
                <tr className="bg-gradient-to-r from-blue-100/50 to-indigo-100/50">
                  <td colSpan="4" className="py-3 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wide">
                    Section A - Main Works
                  </td>
                </tr>

                {/* Section A */}
                <TableRow 
                  label="Preliminaries" 
                  originalValue={data.originalCV.prelims}
                  ipcValue={data.ipc.prelims}
                  ipaValue={data.ipa.prelims}
                />
                <TableRow 
                  label="Work Done" 
                  originalValue={data.originalCV.workDone}
                  ipcValue={data.ipc.workDone}
                  ipaValue={data.ipa.workDone}
                />
                <TableRow 
                  label="Provisional Sums" 
                  originalValue={data.originalCV.provisionalSums}
                  ipcValue={data.ipc.provisionalSums}
                  ipaValue={data.ipa.provisionalSums}
                />
                <TableRow 
                  label="Approved Variation" 
                  originalValue={data.originalCV.approvedVariation}
                  ipcValue={data.ipc.approvedVariation}
                  ipaValue={data.ipa.approvedVariation}
                />
                <TableRow 
                  label="Sub-Total A" 
                  originalValue={calculated.originalCV.subTotalA}
                  ipcValue={calculated.ipc.subTotalA}
                  ipaValue={calculated.ipa.subTotalA}
                  isBold={true}
                  hasBorder={true}
                  isSubtotal={true}
                />

                {/* Section B Header */}
                <tr className="bg-gradient-to-r from-blue-100/50 to-indigo-100/50">
                  <td colSpan="4" className="py-3 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wide">
                    Section B - Adjustments
                  </td>
                </tr>

                {/* Section B */}
                <TableRow 
                  label="Material-on-Site" 
                  originalValue={null}
                  ipcValue={data.ipc.materialOnSite}
                  ipaValue={data.ipa.materialOnSite}
                />
                <TableRow 
                  label="Claims & Other Amounts" 
                  originalValue={null}
                  ipcValue={data.ipc.claimsOtherAmounts}
                  ipaValue={data.ipa.claimsOtherAmounts}
                />
                <TableRow 
                  label="Advances & Adjustments" 
                  originalValue={null}
                  ipcValue={data.ipc.advancesAdjustments}
                  ipaValue={data.ipa.advancesAdjustments}
                />
                <TableRow 
                  label="Deductions/Penalty" 
                  originalValue={null}
                  ipcValue={data.ipc.deductionsPenalty}
                  ipaValue={data.ipa.deductionsPenalty}
                />
                <TableRow 
                  label="Sub-Total B" 
                  originalValue={null}
                  ipcValue={calculated.ipc.subTotalB}
                  ipaValue={calculated.ipa.subTotalB}
                  isBold={true}
                  hasBorder={true}
                  isSubtotal={true}
                />

                {/* Net Calculations */}
                <TableRow 
                  label="Net Payable (A + B)" 
                  originalValue={calculated.originalCV.netPayable}
                  ipcValue={calculated.ipc.netPayable}
                  ipaValue={calculated.ipa.netPayable}
                  isBold={true}
                  hasBorder={true}
                  isSubtotal={true}
                />
                <TableRow 
                  label="Less: Previously Certified" 
                  originalValue={null}
                  ipcValue={data.ipc.prevCertified}
                  ipaValue={data.ipa.prevCertified}
                />
                <TableRow 
                  label="Net Amount Due" 
                  originalValue={null}
                  ipcValue={calculated.ipc.netAmountDue}
                  ipaValue={calculated.ipa.netAmountDue}
                  isBold={true}
                  isSubtotal={true}
                />

                {/* VAT Row */}
                <tr className="transition-all duration-200 hover:bg-blue-50/50 border-t border-gray-100">
                  <td className="py-4 px-6 text-left text-gray-700 bg-gradient-to-r from-gray-50 to-transparent">
                    VAT @ {data.vatPercent}%
                  </td>
                  <td className="py-4 px-6 text-right text-gray-600">
                    {formatNumber(calculated.originalCV.vat)}
                  </td>
                  <td className="py-4 px-6 text-right text-gray-600">
                    {formatNumber(calculated.ipc.vat)}
                  </td>
                  <td className="py-4 px-6 text-right text-gray-600">
                    {formatNumber(calculated.ipa.vat)}
                  </td>
                </tr>

                {/* Final Amount */}
                <tr className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-t-2 border-emerald-200">
                  <td className="py-5 px-6 text-left font-bold text-gray-800 text-lg">
                    Total Payable Amount (incl. VAT)
                  </td>
                  <td className="py-5 px-6 text-right font-bold text-blue-600 text-xl">
                    {formatNumber(calculated.originalCV.payableAmount)}
                  </td>
                  <td className="py-5 px-6 text-right font-bold text-emerald-600 text-xl">
                    {formatNumber(calculated.ipc.payableAmount)}
                  </td>
                  <td className="py-5 px-6 text-right font-bold text-purple-600 text-xl">
                    {formatNumber(calculated.ipa.payableAmount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialDashboard;